<script setup lang="ts">
import { computed, ref } from 'vue'
import { useColorTheme } from '../composables/useColorTheme'
import type { LocaleCode, LocalizedCopy, SiteConfig } from '../types/site'

const props = withDefaults(defineProps<{
  config: SiteConfig
  copy: LocalizedCopy
  locale: LocaleCode
  activePage?: 'home' | 'about'
}>(), {
  activePage: 'home',
})

const emit = defineEmits<{
  localeChange: [locale: LocaleCode]
}>()

const languageOpen = ref(false)
const { theme, toggleTheme } = useColorTheme()

// 字标拆成两段分别着色（例如 Rare + card），避免用百分比渐变裁切文字时切在字母中间
const brandParts = computed(() => {
  const name = props.config.brandName
  const accent = props.config.brandAccent
  if (accent && name.length > accent.length && name.endsWith(accent)) {
    return { head: name.slice(0, name.length - accent.length), accent }
  }
  return { head: name, accent: '' }
})

const themeToggleLabel = computed(() => {
  const lightMode = theme.value === 'dark'
  if (props.locale === 'zh-Hant') return lightMode ? '切換至淺色模式' : '切換至深色模式'
  if (props.locale === 'ko') return lightMode ? '라이트 모드로 전환' : '다크 모드로 전환'
  if (props.locale === 'en') return lightMode ? 'Switch to light mode' : 'Switch to dark mode'
  return lightMode ? '切换到浅色模式' : '切换到暗夜模式'
})

function chooseLocale(locale: LocaleCode) {
  emit('localeChange', locale)
  languageOpen.value = false
}
</script>

<template>
  <header class="app-header" data-track-section="header">
    <a class="app-header__brand" :href="config.links.home" :aria-label="`${config.brandName} home`" data-track="brand-logo">
      {{ brandParts.head }}<span v-if="brandParts.accent" class="app-header__brand-accent">{{ brandParts.accent }}</span>
    </a>

    <nav class="app-header__nav" aria-label="Primary navigation">
      <a
        class="app-header__link"
        :class="{ 'app-header__link--active': activePage === 'home' }"
        :href="config.links.home"
        data-track="nav-home"
      >
        {{ copy.navigation.home }}
      </a>
      <a
        class="app-header__link"
        :class="{ 'app-header__link--active': activePage === 'about' }"
        :href="config.links.about"
        data-track="nav-about"
      >
        {{ copy.navigation.about }}
      </a>
      <button
        class="theme-toggle__button"
        type="button"
        :aria-label="themeToggleLabel"
        :title="themeToggleLabel"
        data-track="theme-toggle"
        @click="toggleTheme"
      >
        <svg v-if="theme === 'dark'" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.93 4.93l1.42 1.42M17.65 17.65l1.42 1.42M2 12h2M20 12h2M4.93 19.07l1.42-1.42M17.65 6.35l1.42-1.42" />
        </svg>
        <svg v-else viewBox="0 0 24 24" aria-hidden="true">
          <path d="M20.3 15.1A8.4 8.4 0 0 1 8.9 3.7 8.5 8.5 0 1 0 20.3 15.1Z" />
        </svg>
      </button>
      <div class="language-picker">
        <button
          class="language-picker__button"
          type="button"
          :aria-label="copy.navigation.language"
          :aria-expanded="languageOpen"
          data-track="language-toggle"
          @click="languageOpen = !languageOpen"
        >
          <img :src="config.media.languageIcon" alt="" />
        </button>
        <div v-if="languageOpen" class="language-picker__menu">
          <button
            v-for="language in config.languages"
            :key="language.code"
            type="button"
            :class="{ active: language.code === locale }"
            :data-track="`language:${language.code}`"
            @click="chooseLocale(language.code)"
          >
            {{ language.label }}
          </button>
        </div>
      </div>
    </nav>
  </header>
</template>
